/**
 * Explore Catalog Store
 *
 * Manages per-tenant catalog cache, loading state, and error state.
 * Caches by tenantId — no refetch when tenant hasn't changed.
 */

import { create } from 'zustand'
import { NormalizedExploreCatalog } from '../core/types/explore.types'
import { normalizeExploreCatalog } from '../core/utils/explore-catalog'
import { IExploreCatalogService } from '../core/services/exploreCatalogService'

type MaxExploreCatalogStoreState = {
  catalogByTenant: Map<string, NormalizedExploreCatalog>
  isLoading: boolean
  error: string | null
}

type MaxExploreCatalogStoreActions = {
  getCatalog: (tenantId: string) => NormalizedExploreCatalog | null
  loadCatalog: (tenantId: string, service: IExploreCatalogService) => Promise<void>
  clearCatalog: (tenantId?: string) => void
}

type MaxExploreCatalogStore = MaxExploreCatalogStoreState & MaxExploreCatalogStoreActions

const useMaxExploreCatalogStore = create<MaxExploreCatalogStore>((set, get) => ({
  catalogByTenant: new Map(),
  isLoading: false,
  error: null,

  getCatalog: (tenantId) => {
    return get().catalogByTenant.get(tenantId) ?? null
  },

  loadCatalog: async (tenantId, service) => {
    if (get().catalogByTenant.has(tenantId)) return

    set({ isLoading: true, error: null })

    try {
      const raw = await service.getCatalog(tenantId)
      const normalized = normalizeExploreCatalog(raw)
      set((state) => {
        const next = new Map(state.catalogByTenant)
        next.set(tenantId, normalized)
        return { catalogByTenant: next, isLoading: false }
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load Explore catalog'
      set({ isLoading: false, error: message })
    }
  },

  clearCatalog: (tenantId) => {
    if (tenantId) {
      set((state) => {
        const next = new Map(state.catalogByTenant)
        next.delete(tenantId)
        return { catalogByTenant: next }
      })
    } else {
      set({ catalogByTenant: new Map() })
    }
  },
}))

export default useMaxExploreCatalogStore
